import { useEffect, useMemo, useState } from "react";
import { wfAPI } from "./wfApi";
import { toast } from "sonner";
import { Search, UserPlus, Mail, Phone, ShieldCheck, MoreHorizontal } from "lucide-react";
import EmployeeDrawer from "./EmployeeDrawer";
import CreateEmployeeDialog from "./CreateEmployeeDialog";

const STATUSES = [
  { v: "",           label: "All" },
  { v: "active",     label: "Active" },
  { v: "suspended",  label: "Suspended" },
  { v: "terminated", label: "Terminated" },
];

export default function Employees() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active");
  const [orgUnitFilter, setOrgUnitFilter] = useState("");
  const [items, setItems] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rows, ous] = await Promise.all([
        wfAPI.employees({ q: q || undefined, status: status || undefined, org_unit: orgUnitFilter || undefined }),
        wfAPI.orgUnits(),
      ]);
      setItems(rows);
      setOrgUnits(ous);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status, orgUnitFilter]);

  return (
    <div className="space-y-4" data-testid="wf-employees-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[280px] max-w-[420px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.75}/>
          <input
            data-testid="employees-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] outline-none focus:border-hocp-navy transition"
            placeholder="Search by name or email"
          />
        </div>
        <select data-testid="employees-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <select data-testid="employees-org-filter" value={orgUnitFilter} onChange={(e) => setOrgUnitFilter(e.target.value)}
          className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px]">
          <option value="">All departments</option>
          {orgUnits.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <div className="flex-1"/>
        <button
          onClick={() => setCreateOpen(true)}
          data-testid="employees-create"
          className="h-10 inline-flex items-center gap-2 px-4 rounded-lg bg-hocp-gold text-hocp-navy text-[13px] font-medium hover:brightness-95"
        >
          <UserPlus className="w-4 h-4" strokeWidth={1.75}/>
          Add Employee
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[minmax(240px,2fr)_1.5fr_1.5fr_1fr_1fr_40px] gap-4 px-5 py-3 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
          <span>Name</span>
          <span>Role</span>
          <span>Department</span>
          <span>Contact</span>
          <span>Status</span>
          <span/>
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center text-slate-400 text-[13px]">Loading…</div>
        ) : !items.length ? (
          <div className="px-5 py-16 text-center">
            <div className="text-slate-500 text-[14px] mb-1">No employees match these filters.</div>
            <div className="text-slate-400 text-[12px]">Try clearing filters, or add a new employee.</div>
          </div>
        ) : (
          items.map((e) => (
            <button key={e.id} onClick={() => setDrawerId(e.id)}
              data-testid={`employee-row-${e.id}`}
              className="grid grid-cols-[minmax(240px,2fr)_1.5fr_1.5fr_1fr_1fr_40px] gap-4 items-center px-5 py-3.5 text-[13px] border-b border-slate-100 hover:bg-slate-50/70 text-left w-full transition-colors last:border-b-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 font-semibold text-[12px] shrink-0">
                  {(e.name || e.email || "?").slice(0,2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-hocp-navy truncate">{e.name || "—"}</div>
                  <div className="text-[11.5px] text-slate-400 truncate">{e.employee_id || e.email}</div>
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.75}/>
                  <span className="truncate">{e.role_template_name || e.role || "—"}</span>
                </div>
                <div className="text-[11.5px] text-slate-400 truncate">{e.permission_profile_code || ""}</div>
              </div>
              <div className="truncate text-slate-700">
                {e.primary_org_unit_name || <span className="text-slate-400">—</span>}
                {e.designation && <div className="text-[11.5px] text-slate-400 truncate">{e.designation}</div>}
              </div>
              <div className="text-[12px] text-slate-500 truncate">
                <div className="flex items-center gap-1.5"><Mail className="w-3 h-3"/> {e.email}</div>
                {e.phone && <div className="flex items-center gap-1.5 mt-0.5"><Phone className="w-3 h-3"/> {e.phone}</div>}
              </div>
              <div>
                <span data-testid={`employee-status-${e.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11.5px] font-medium
                  ${e.employment_status === "active" ? "bg-emerald-50 text-emerald-700"
                    : e.employment_status === "suspended" ? "bg-amber-50 text-amber-700"
                    : "bg-rose-50 text-rose-700"}`}>
                  {e.employment_status}
                </span>
              </div>
              <MoreHorizontal className="w-4 h-4 text-slate-300"/>
            </button>
          ))
        )}
      </div>

      {drawerId && (
        <EmployeeDrawer employeeId={drawerId} onClose={() => { setDrawerId(null); load(); }} />
      )}
      {createOpen && (
        <CreateEmployeeDialog onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />
      )}
    </div>
  );
}
